class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // v24 FIX: track whether the overlay listener has been bound so we
    // don't add duplicate handlers every time renderContents() runs.
    this._overlayBound = true;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // v24 FIX: rAF so the class add happens after the current paint tick,
    // giving the browser a chance to coalesce any pending style changes.
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    // v24 FIX: transitionend fires for EVERY CSS property change
    // (background-color, opacity, transform, etc.) — with { once: true }
    // it almost always fires on the wrong property, trapping focus
    // mid-transition and forcing a synchronous layout read while the
    // transform is animating. Use a fixed timeout that matches the
    // drawer's CSS transition duration instead.
    this._focusTrapTimer && clearTimeout(this._focusTrapTimer);
    this._focusTrapTimer = setTimeout(() => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (containerToTrapFocusOn && focusElement) {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    }, 250); // matches --duration-medium used by Dawn's cart drawer

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    // v24 FIX: cancel any pending focus-trap setup if the drawer
    // closes before the animation completes.
    if (this._focusTrapTimer) {
      clearTimeout(this._focusTrapTimer);
      this._focusTrapTimer = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;

    // v24 FIX: batch all innerHTML writes into an array first, then apply
    // them in a single pass. Previously each iteration forced its own
    // style recalc because we wrote directly to live nodes.
    const sectionsToRender = this.getSectionsToRender();
    const pendingWrites = [];

    sectionsToRender.forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      pendingWrites.push({
        element: sectionElement,
        html: this.getSectionInnerHTML(parsedState.sections[section.id], section.selector),
      });
    });

    pendingWrites.forEach(({ element, html }) => {
      element.innerHTML = html;
    });

    // v24 FIX: guard against duplicate overlay listeners.
    if (!this._overlayBound) {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
        this._overlayBound = true;
      }
    }

    // v24 FIX: rAF then open. Gives the browser a paint cycle to
    // settle the innerHTML writes before the animation class is added.
    requestAnimationFrame(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);